import type { ReactElement, ReactNode } from 'react';

/**
 * The chrome every showcase visual sits in — a stylised application window.
 *
 * ## Why it is stylised rather than a screenshot
 *
 * A screenshot of a customer's product would be either a real customer's data
 * (which we do not have permission to publish) or invented sample data, which
 * `quality-bar.md` forbids in shipped code and which a reader recognises as
 * fake the moment they read a row. A wireframe promises nothing about whose
 * product it is: it says "your product" without pretending to BE anybody's.
 *
 * It also survives translation. A screenshot is one language forever; this is
 * geometry, with the only text coming from the catalogs.
 *
 * ## No caption in the title bar
 *
 * The bar carries the three dots and nothing else. It used to caption itself —
 * "Syncanix · capability review", "Syncanix · reading your product",
 * "Your product" — and both halves of that were wrong. Naming the TOOL on a
 * frame whose entire job is to stand in for the reader's own product argues
 * against the wireframe; and every one of these visuals already sits under a
 * pillar heading and beside the sentence it illustrates, so the caption was a
 * second, smaller, competing title a few pixels above the real one.
 *
 * `data-frame-chrome` exists so `Features.test.tsx` can assert the bar stays
 * empty. Asserting on the ABSENT strings would pass whether or not the element
 * still renders something else.
 *
 * ## Decoration, all of it
 *
 * Every visual on this page is `aria-hidden`, and each showcase block carries
 * the same claim in prose beside it. A screen-reader user is not shown a
 * diagram they cannot read and told to make do — they are given the sentence
 * the diagram illustrates, which is the sentence sighted readers get too.
 */
export function Frame({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}): ReactElement {
  return (
    <div
      className={[
        'overflow-hidden rounded-[15px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)]',
        'shadow-[0_28px_60px_-40px_rgb(15_23_42/0.35)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        data-frame-chrome
        className="flex items-center gap-2.5 border-b border-[color:var(--bd-line-2)] bg-[color:var(--bd-sunk)] px-4 py-2.5"
      >
        <span aria-hidden="true" className="flex gap-1.5">
          {['#f0554f', '#f6bd4f', '#5ac05a'].map((dot) => (
            <span key={dot} className="size-[9px] rounded-full" style={{ background: dot }} />
          ))}
        </span>
      </div>
      {children}
    </div>
  );
}

/**
 * A wireframe block — the grey furniture a real screen is mostly made of.
 *
 * Sized in `rem` rather than `px` so the whole diagram scales with the reader's
 * font size instead of staying pinned while the prose beside it grows.
 */
export function Bar({
  w,
  h = 0.5,
  className,
}: {
  readonly w: string;
  readonly h?: number;
  readonly className?: string;
}): ReactElement {
  return (
    <span
      aria-hidden="true"
      className={['block rounded-full bg-[color:var(--bd-line-2)]', className]
        .filter(Boolean)
        .join(' ')}
      style={{ width: w, height: `${h}rem` }}
    />
  );
}
